import React, { useState, useMemo } from 'react';
import { Search, Download, ChevronUp, ChevronDown, ChevronsUpDown, FileText, FileImage, FileArchive, File, Layers, Activity, Flag, FolderOpen, Trash2, X, BookOpen } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Button, Modal } from '@/components/common';
import { SearchableSelect } from '../common/SearchableSelect';
import { deleteAttachment, downloadAttachmentFile, downloadAttachmentsAsZip } from '@/services/attachments.service';
import AddActivityForDocumentButton from '../dossier/common/AddActivityForDocumentButton';
import type { ProjectAttachment } from '@/services/projects.service';

interface ProjectFilesTableProps {
  attachments: ProjectAttachment[];
  baseUrl?: string;
  currentUserId?: number;
  onFileDeleted?: () => void;
  // Necesar pentru butonul „Adaugă activitate" de pe fiecare rând.
  projectId?: number;
}

type SortField = 'fileName' | 'createdAt' | 'fileSize' | 'author' | 'container' | 'source';
type SortDirection = 'asc' | 'desc' | null;

// Sursa (modulul) unui fișier -> etichetă + clase badge colorat
type SourceBadge = { label: string; className: string };

function getSourceBadge(att: ProjectAttachment): SourceBadge {
  // Backend-ul poate întoarce `source` sau `source_module`.
  const raw = (att.source || att.source_module || '').toString().toLowerCase();

  switch (raw) {
    case 'procurement':
    case 'procurements':
    case 'achizitii':
    case 'achiziții':
      return { label: 'Achiziții', className: 'bg-orange-100 text-orange-700 border border-orange-200' };
    case 'contract':
    case 'contracts':
    case 'contracte':
      return { label: 'Contracte', className: 'bg-blue-100 text-blue-700 border border-blue-200' };
    case 'deliverable':
    case 'deliverables':
    case 'livrabile':
      return { label: 'Livrabile', className: 'bg-purple-100 text-purple-700 border border-purple-200' };
    case 'implementation':
    case 'implementare':
      return { label: 'Implementare', className: 'bg-green-100 text-green-700 border border-green-200' };
    case 'openproject':
    case '':
    default:
      return { label: 'OpenProject', className: 'bg-gray-100 text-gray-600 border border-gray-200' };
  }
}

// Get work package type icon and label
function getWorkPackageTypeInfo(container?: NonNullable<ProjectAttachment['_embedded']>['container']) {
  if (!container) {
    return { icon: <FolderOpen className="w-4 h-4 text-gray-400" />, label: 'Proiect', color: 'text-gray-500' };
  }

  // Dossier document
  if (container._type === 'DossierDocument') {
    const section = (container as any).dossierSection || 'dosar';
    const colors: Record<string, string> = {
      achizitii: 'text-orange-600',
      contracte: 'text-blue-600',
      livrabile: 'text-purple-600',
      implementare: 'text-green-600',
    };
    return { icon: <BookOpen className="w-4 h-4 text-indigo-500" />, label: 'Dosar investiție', color: colors[section] || 'text-indigo-600' };
  }

  const wpType = container.workPackageType;
  if (!wpType) {
    // Translate container._type from English to Romanian
    const typeLabel = container._type === 'WorkPackage' ? 'Activitate' : (container._type || 'Element');
    return { icon: <Activity className="w-4 h-4 text-gray-400" />, label: typeLabel, color: 'text-gray-500' };
  }

  // Check if it's a milestone
  if (wpType.isMilestone) {
    return { icon: <Flag className="w-4 h-4 text-purple-500" />, label: wpType.name, color: 'text-purple-600' };
  }

  // Check by name patterns
  const typeName = wpType.name.toLowerCase();
  if (typeName.includes('fază') || typeName.includes('faza') || typeName.includes('phase')) {
    return { icon: <Layers className="w-4 h-4 text-blue-500" />, label: wpType.name, color: 'text-blue-600' };
  }
  if (typeName.includes('activitate') || typeName.includes('activity') || typeName.includes('task')) {
    return { icon: <Activity className="w-4 h-4 text-green-500" />, label: wpType.name, color: 'text-green-600' };
  }
  if (typeName.includes('reper') || typeName.includes('milestone')) {
    return { icon: <Flag className="w-4 h-4 text-purple-500" />, label: wpType.name, color: 'text-purple-600' };
  }

  // Default
  return { icon: <Layers className="w-4 h-4 text-gray-500" />, label: wpType.name, color: 'text-gray-600' };
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Get file icon based on content type
function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) {
    return <FileImage className="w-5 h-5 text-green-500" />;
  }
  if (contentType.includes('pdf')) {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed')) {
    return <FileArchive className="w-5 h-5 text-yellow-500" />;
  }
  if (contentType.includes('word') || contentType.includes('document')) {
    return <FileText className="w-5 h-5 text-blue-500" />;
  }
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) {
    return <FileText className="w-5 h-5 text-green-600" />;
  }
  return <File className="w-5 h-5 text-gray-500" />;
}

export function ProjectFilesTable({ attachments, baseUrl = '', currentUserId, onFileDeleted, projectId }: ProjectFilesTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Delete modal state
  const [fileToDelete, setFileToDelete] = useState<ProjectAttachment | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter attachments by search term
  const filteredAttachments = useMemo(() => {
    if (!searchTerm.trim()) return attachments;

    const term = searchTerm.toLowerCase();
    return attachments.filter(att => {
      const authorName = att._embedded?.author?.name || '';
      const containerSubject = att._embedded?.container?.subject || '';
      const sourceLabel = getSourceBadge(att).label;
      return (
        att.fileName.toLowerCase().includes(term) ||
        authorName.toLowerCase().includes(term) ||
        containerSubject.toLowerCase().includes(term) ||
        sourceLabel.toLowerCase().includes(term) ||
        (att.contentType && att.contentType.toLowerCase().includes(term))
      );
    });
  }, [attachments, searchTerm]);

  // Sort attachments
  const sortedAttachments = useMemo(() => {
    if (!sortField || !sortDirection) return filteredAttachments;

    return [...filteredAttachments].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'fileName':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
        case 'fileSize':
          aValue = a.fileSize;
          bValue = b.fileSize;
          break;
        case 'author':
          aValue = a._embedded?.author?.name?.toLowerCase() || '';
          bValue = b._embedded?.author?.name?.toLowerCase() || '';
          break;
        case 'container':
          aValue = a._embedded?.container?.subject?.toLowerCase() || '';
          bValue = b._embedded?.container?.subject?.toLowerCase() || '';
          break;
        case 'source':
          aValue = getSourceBadge(a).label.toLowerCase();
          bValue = getSourceBadge(b).label.toLowerCase();
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAttachments, sortField, sortDirection]);

  // Paginate
  const paginatedAttachments = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedAttachments.slice(start, start + pageSize);
  }, [sortedAttachments, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedAttachments.length / pageSize);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className="w-4 h-4 text-primary-600" />;
    }
    return <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  // Handle checkbox selection
  const handleSelectAll = () => {
    if (selectedIds.size === paginatedAttachments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedAttachments.map(a => a.id)));
    }
  };

  const handleSelectOne = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Handle download using authenticated request
  const handleDownload = async (attachment: ProjectAttachment) => {
    try {
      // Documentele din modulele de dosar pot furniza un URL direct de descărcare.
      if (attachment.downloadUrl) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = attachment.downloadUrl;
        document.body.appendChild(iframe);
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 60000);
        return;
      }
      // Descărcare standard prin attachment_id (funcționează și pentru documentele de dosar).
      await downloadAttachmentFile(attachment.id, attachment.fileName);
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  // Handle bulk download as ZIP archive
  const handleBulkDownload = async () => {
    const selectedAttachmentIds = Array.from(selectedIds);
    if (selectedAttachmentIds.length === 0) return;

    setIsDownloading(true);
    setError(null);
    try {
      const archiveName = `fisiere_proiect_${new Date().toISOString().split('T')[0]}.zip`;
      await downloadAttachmentsAsZip(selectedAttachmentIds, archiveName);
    } catch (err) {
      console.error('Failed to download files as ZIP:', err);
      const errorMessage = err instanceof Error ? err.message : 'Eroare la descărcarea arhivei ZIP. Încercați din nou.';
      setError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle file delete
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      await deleteAttachment(fileToDelete.id, deleteReason || undefined);
      setFileToDelete(null);
      setDeleteReason('');
      onFileDeleted?.();
    } catch (err) {
      console.error('Failed to delete file:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if user can delete a file (only their own uploads)
  const canDeleteFile = (attachment: ProjectAttachment) => {
    if (!currentUserId) return false;
    // Documentele din module (Achiziții/Contracte/Livrabile/Implementare) NU se șterg de aici:
    // ștergerea prin /attachments ar lăsa înregistrarea din dosar orfană (fișier 404). Se șterg din modulul lor.
    const src = (attachment.source_module || attachment.source || 'openproject').toString().toLowerCase();
    const isOpenProjectNative = src === 'openproject' || src === '' || src === 'activitate' || src === 'proiect';
    if (!isOpenProjectNative) return false;
    return attachment._embedded?.author?.id === currentUserId;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  const isAllSelected = paginatedAttachments.length > 0 && selectedIds.size === paginatedAttachments.length;
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="space-y-4">
      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header cu căutare, acțiuni și dimensiune pagină */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută fișiere..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {hasSelection && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleBulkDownload}
              isLoading={isDownloading}
              disabled={isDownloading}
            >
              {isDownloading ? 'Se generează arhiva...' : `Descarcă (${selectedIds.size})`}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Afișează</span>
          <SearchableSelect
            clearable={false}
            options={[
              { value: 5, label: '5' },
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
            ]}
            value={pageSize}
            onChange={(v) => {
              setPageSize(Number(v));
              setCurrentPage(1);
            }}
            className="w-20"
          />
          <span>înregistrări</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('fileName')}
              >
                <div className="flex items-center gap-1">
                  Document
                  <SortIcon field="fileName" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('container')}
              >
                <div className="flex items-center gap-1">
                  Activitate
                  <SortIcon field="container" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('source')}
              >
                <div className="flex items-center gap-1">
                  Sursă
                  <SortIcon field="source" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center gap-1">
                  Data încărcării
                  <SortIcon field="createdAt" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('fileSize')}
              >
                <div className="flex items-center gap-1">
                  Mărime
                  <SortIcon field="fileSize" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('author')}
              >
                <div className="flex items-center gap-1">
                  Încărcat de
                  <SortIcon field="author" />
                </div>
              </th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 w-32">
                Acțiuni
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedAttachments.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  {searchTerm ? 'Nu s-au găsit rezultate pentru căutarea efectuată.' : 'Nu există fișiere în acest proiect.'}
                </td>
              </tr>
            ) : (
              paginatedAttachments.map((att) => {
                const authorName = att._embedded?.author?.name || '-';
                const containerInfo = att._embedded?.container;
                const typeInfo = getWorkPackageTypeInfo(containerInfo);

                return (
                  <tr
                    key={att.id}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(att.id)}
                        onChange={() => handleSelectOne(att.id)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {getFileIcon(att.contentType || '')}
                        <span className="font-medium text-gray-900">
                          {att.fileName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {typeInfo.icon}
                        <div>
                          {containerInfo ? (
                            <>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[250px] block" title={containerInfo.subject}>
                                {containerInfo.subject}
                              </span>
                              <span className={`text-xs ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                            </>
                          ) : (
                            <span className={`text-sm font-medium ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const badge = getSourceBadge(att);
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.className}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDate(att.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatFileSize(att.fileSize)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {authorName}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(att)}
                          title="Descarcă"
                        >
                          <Download className="w-4 h-4 text-primary-600" />
                        </Button>
                        {projectId && att.id && (
                          <AddActivityForDocumentButton
                            projectId={projectId}
                            attachmentId={att.id}
                            fileName={att.fileName}
                            variant="icon"
                            size="sm"
                            onCreated={onFileDeleted}
                          />
                        )}
                        {canDeleteFile(att) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFileToDelete(att)}
                            title="Șterge"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer cu paginare */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-600">
          Afișare {sortedAttachments.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, sortedAttachments.length)} din {sortedAttachments.length} fișiere
          {searchTerm && ` (filtrat din ${attachments.length} total)`}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &lsaquo;
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === pageNum
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &rsaquo;
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        )}
      </div>

      {/* Modal confirmare ștergere fișier */}
      <Modal
        isOpen={!!fileToDelete}
        onClose={() => {
          setFileToDelete(null);
          setDeleteReason('');
        }}
        title="Confirmare ștergere fișier"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setFileToDelete(null);
                setDeleteReason('');
              }}
            >
              Anulează
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteFile}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Șterge
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sunteți sigur că doriți să ștergeți fișierul{' '}
            <strong>{fileToDelete?.fileName}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Această acțiune nu poate fi anulată.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motiv ștergere (opțional)
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Introduceți motivul ștergerii..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              rows={3}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
